import React, { useRef } from 'react';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  hasError?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  length = 6,
  hasError = false,
}) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const focusBox = (index: number) => {
    refs.current[Math.min(Math.max(index, 0), length - 1)]?.focus();
  };

  const handleChange = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (digits === '') return;
    const chars = value.padEnd(length, ' ').split('');
    let cursor = index;
    for (const digit of digits) {
      if (cursor >= length) break;
      chars[cursor] = digit;
      cursor += 1;
    }
    onChange(chars.join('').replace(/ /g, ''));
    focusBox(cursor);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (value[index] !== undefined) {
        onChange(value.slice(0, index) + value.slice(index + 1));
      } else {
        onChange(value.slice(0, Math.max(index - 1, 0)));
        focusBox(index - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      focusBox(index - 1);
    } else if (e.key === 'ArrowRight') {
      focusBox(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length);
    if (digits === '') return;
    onChange(digits);
    focusBox(digits.length);
  };

  return (
    <div className="otp-input">
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          className={`otp-box${value[index] !== undefined ? ' filled' : ''}${
            hasError ? ' error' : ''
          }`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          value={value[index] ?? ''}
          aria-label={`Digit ${index + 1}`}
          onChange={(e) => {
            handleChange(index, e.target.value);
          }}
          onKeyDown={(e) => {
            handleKeyDown(index, e);
          }}
          onPaste={handlePaste}
          onFocus={(e) => {
            e.target.select();
          }}
        />
      ))}
    </div>
  );
};

export default OtpInput;
